import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Men.css';
import appointmentService from '../../services/appointment.service';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface Service {
  id: number;
  name: string;
  price: number;
  duration: number;
}

interface TimeSlot {
  time: string;
  blocked: boolean;
}

const AVAILABILITY_REFRESH_MS = 15000;

const OPENING_HOURS: { [key: number]: { open: string; close: string } | null } = {
  0: null,
  1: null,
  2: { open: '10:00', close: '20:00' },
  3: { open: '10:00', close: '17:00' },
  4: { open: '10:00', close: '20:00' },
  5: { open: '10:00', close: '20:00' },
  6: { open: '10:00', close: '20:00' },
};

const toMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatDate = (selectedDate: Date) => {
  const year = selectedDate.getFullYear();
  const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
  const day = String(selectedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDayClosed = (date: Date) => OPENING_HOURS[date.getDay()] === null;

const generateTimeSlots = (selectedDate: Date, duration: number) => {
  const dayConfig = OPENING_HOURS[selectedDate.getDay()];
  if (!dayConfig) return [];

  const slots: string[] = [];
  const [openHour, openMinute] = dayConfig.open.split(':').map(Number);
  const [closeHour, closeMinute] = dayConfig.close.split(':').map(Number);

  const current = new Date(selectedDate);
  current.setHours(openHour, openMinute, 0, 0);

  const closeTime = new Date(selectedDate);
  closeTime.setHours(closeHour, closeMinute, 0, 0);

  while (true) {
    const potentialEnd = new Date(current.getTime() + duration * 60000);
    if (potentialEnd > closeTime) break;

    slots.push(
      current.toLocaleTimeString('el-GR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    );

    current.setMinutes(current.getMinutes() + 15);
  }

  return slots;
};

const hasBufferedConflict = (
  time: string,
  duration: number,
  bookedIntervals: { start: number; end: number }[]
) => {
  const requestedStart = toMinutes(time);
  const requestedEnd = requestedStart + duration;

  return bookedIntervals.some(interval => {
    return requestedStart < interval.end && requestedEnd > interval.start;
  });
};

const mapAppointmentsToAvailability = (appointments: any[]) => {
  const intervals = appointments
    .filter(app => app.status !== 'CANCELLED')
    .map(app => {
      const startTime = app.time?.slice(0, 5);
      if (!startTime) return null;

      const durationMinutes = Math.max(app.durationMinutes ?? app.duration ?? 0, 1);
      const start = toMinutes(startTime);
      const end = start + durationMinutes;

      return { start, end };
    })
    .filter((interval): interval is { start: number; end: number } => interval !== null);

  const dayBlocked = appointments.some(
    app => app.status === 'BLOCKED' && ((app.durationMinutes ?? app.duration ?? 0) >= 24 * 60)
  );

  return { intervals, dayBlocked };
};

const loadAvailabilityForDate = async (selectedDate: Date) => {
  const appointments = await appointmentService.getAppointmentsForDate(formatDate(selectedDate));
  return mapAppointmentsToAvailability(appointments);
};

const Men: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState<boolean>(true);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [date, setDate] = useState<Value>(new Date());
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookedIntervals, setBookedIntervals] = useState<{ start: number; end: number }[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isDayBlocked, setIsDayBlocked] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    setServicesLoading(true);
    setServicesError(null);

    appointmentService.getPackages()
      .then((data: any[]) => {
        const mapped = data
          .map(pkg => {
            const durationMinutes = pkg.durationMinutes ?? pkg.duration_minutes ?? pkg.duration ?? 0;
            const requiresCall = pkg.requiresCall ?? pkg.requires_call ?? false;

            return {
              id: pkg.id,
              name: pkg.name ?? pkg.serviceName ?? '',
              price: pkg.price ?? 0,
              duration: durationMinutes,
              requiresCall
            };
          })
          .filter(pkg => pkg.duration > 0 && !pkg.requiresCall && pkg.name)
          .map(pkg => ({
            id: pkg.id,
            name: pkg.name,
            price: pkg.price,
            duration: pkg.duration
          }));

        setServices(mapped);
      })
      .catch(() => {
        setServices([]);
        setServicesError('Failed to load services. Please try again later.');
      })
      .finally(() => setServicesLoading(false));
  }, []);

  useEffect(() => {
    if (!(date instanceof Date)) {
      setBookedIntervals([]);
      setIsDayBlocked(false);
      return;
    }

    let cancelled = false;

    const refreshAvailability = async () => {
      try {
        const { intervals, dayBlocked } = await loadAvailabilityForDate(date);
        if (!cancelled) {
          setBookedIntervals(intervals);
          setIsDayBlocked(dayBlocked);
        }
      } catch {
        if (!cancelled) {
          setBookedIntervals([]);
          setIsDayBlocked(false);
        }
      }
    };

    void refreshAvailability();

    const intervalId = window.setInterval(() => {
      void refreshAvailability();
    }, AVAILABILITY_REFRESH_MS);

    const handleFocus = () => {
      void refreshAvailability();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [date]);

  useEffect(() => {
    if (!(date instanceof Date) || !selectedService || isDayBlocked) {
      setTimeSlots([]);
      if (selectedTime) {
        setSelectedTime(null);
      }
      return;
    }

    const nextTimeSlots = generateTimeSlots(date, selectedService.duration).map(time => ({
      time,
      blocked: hasBufferedConflict(time, selectedService.duration, bookedIntervals)
    }));

    setTimeSlots(nextTimeSlots);

    if (selectedTime) {
      const isStillAvailable = nextTimeSlots.some(slot => slot.time === selectedTime && !slot.blocked);
      if (!isStillAvailable) {
        setSelectedTime(null);
      }
    }
  }, [date, selectedService, bookedIntervals, selectedTime, isDayBlocked]);

  const handleDateChange = (nextValue: Value) => {
    setDate(nextValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBookAppointment = async () => {
    if (!date || !selectedTime || !selectedService) {
      alert('Please select an available time.');
      return;
    }

    if (isDayBlocked) {
      alert('This day is blocked for appointments.');
      return;
    }

    if (!userDetails.firstName || !userDetails.lastName || !userDetails.phone || !userDetails.email) {
      alert('Please fill in all your details.');
      return;
    }

    const selectedDate = date instanceof Date ? date : new Date();
    setIsSubmitting(true);

    try {
      const { intervals, dayBlocked } = await loadAvailabilityForDate(selectedDate);
      setBookedIntervals(intervals);
      setIsDayBlocked(dayBlocked);

      if (dayBlocked || hasBufferedConflict(selectedTime, selectedService.duration, intervals)) {
        setSelectedTime(null);
        alert('Η διαθεσιμότητα άλλαξε. Παρακαλώ επιλέξτε νέα ώρα.');
        return;
      }

      await appointmentService.createAppointment(
        selectedService.name,
        formatDate(selectedDate),
        selectedTime,
        selectedService.price,
        selectedService.duration,
        userDetails.firstName,
        userDetails.lastName,
        userDetails.email,
        userDetails.phone,
        undefined
      );

      alert('Το ραντεβού καταχωρήθηκε επιτυχώς!');
      setSelectedService(null);
      setSelectedTime(null);
      setDate(new Date());
      setUserDetails({ firstName: '', lastName: '', phone: '', email: '' });
    } catch (err) {
      console.error('Booking error:', err);
      alert(err instanceof Error ? err.message : 'Failed to book appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="men-booking-container">
      <div className="booking-wrapper">
        <h2 className="booking-title">Ανδρικό Κομμωτήριο - Κράτηση</h2>
        <p className="complimentary-text">* Το λούσιμο είναι δωρεάν</p>

        <div className="booking-content">
          <div className="section service-section">
            <h3>1. Επιλέξτε Υπηρεσία</h3>
            <div className="services-list">
              {servicesLoading && <p className="no-slots">Φορτώνω υπηρεσίες...</p>}
              {!servicesLoading && servicesError && <p className="no-slots">{servicesError}</p>}
              {!servicesLoading && !servicesError && services.length === 0 && (
                <p className="no-slots">Δεν υπάρχουν διαθέσιμες υπηρεσίες.</p>
              )}
              {services.map(service => (
                <div
                  key={service.id}
                  className={`service-card ${selectedService?.id === service.id ? 'active' : ''}`}
                  onClick={() => setSelectedService(service)}
                >
                  <div className="service-info">
                    <span className="service-name">{service.name}</span>
                    <span className="service-duration">{service.duration} λεπτά</span>
                  </div>
                  <span className="service-price">{service.price}€</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`section calendar-section ${!selectedService ? 'disabled' : ''}`}>
            <h3>2. Επιλέξτε Ημερομηνία</h3>
            <Calendar
              onChange={handleDateChange}
              value={date}
              className="react-calendar-custom"
              tileDisabled={({ date }) => isDayClosed(date)}
              minDate={new Date()}
            />
          </div>

          <div className={`section time-section ${!selectedService ? 'disabled' : ''}`}>
            <h3>3. Επιλέξτε Ώρα</h3>
            <p className="selected-date-label">
              {date instanceof Date ? date.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
              {date instanceof Date && OPENING_HOURS[date.getDay()]
                ? ` (${OPENING_HOURS[date.getDay()]?.open} - ${OPENING_HOURS[date.getDay()]?.close})`
                : ' (Κλειστά)'}
            </p>

            {selectedService && date instanceof Date ? (
              isDayBlocked ? (
                <p className="no-slots">Η ημέρα είναι μπλοκαρισμένη για ραντεβού.</p>
              ) : (
                <div className="time-slots-grid">
                  {timeSlots.map(({ time, blocked }) => (
                    <button
                      key={time}
                      className={`time-slot ${selectedTime === time ? 'selected' : ''} ${blocked ? 'unavailable' : ''}`}
                      disabled={blocked}
                      tabIndex={blocked ? -1 : 0}
                      onClick={blocked ? undefined : () => setSelectedTime(time)}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <p className="no-slots">Παρακαλώ επιλέξτε υπηρεσία και έγκυρη ημερομηνία.</p>
            )}
          </div>
        </div>

        {selectedService && selectedTime && (
          <div className="section details-section">
            <h3>4. Τα Στοιχεία Σας</h3>
            <div className="details-form">
              <div className="form-group">
                <label>Όνομα</label>
                <input
                  type="text"
                  name="firstName"
                  value={userDetails.firstName}
                  onChange={handleInputChange}
                  placeholder="Όνομα"
                />
              </div>
              <div className="form-group">
                <label>Επώνυμο</label>
                <input
                  type="text"
                  name="lastName"
                  value={userDetails.lastName}
                  onChange={handleInputChange}
                  placeholder="Επώνυμο"
                />
              </div>
              <div className="form-group">
                <label>Τηλέφωνο</label>
                <input
                  type="tel"
                  name="phone"
                  value={userDetails.phone}
                  onChange={handleInputChange}
                  placeholder="Τηλέφωνο"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={userDetails.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                />
              </div>
            </div>
          </div>
        )}

        {selectedService && selectedTime && (
          <div className="booking-actions">
            <div className="summary">
              <p>Υπηρεσία: <strong>{selectedService.name}</strong></p>
              <p>Ημερομηνία: <strong>{date instanceof Date ? date.toLocaleDateString() : ''}</strong> στις <strong>{selectedTime}</strong></p>
              <p>Σύνολο: <strong>{selectedService.price}€</strong></p>
            </div>
            <button className="book-btn" onClick={() => void handleBookAppointment()} disabled={isSubmitting}>
              {isSubmitting ? 'ΕΛΕΓΧΟΣ ΔΙΑΘΕΣΙΜΟΤΗΤΑΣ...' : 'ΕΠΙΒΕΒΑΙΩΣΗ ΚΡΑΤΗΣΗΣ'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Men;
